import { useEffect, useState } from 'react';
import {
  fill, RECORD_CHECK_FIELD_LABELS, Size, detailLines, formatDateTime, recordCheckActPath, recordChecksPath, t,
  type DateFormat, type JobEntryKind, type OtherNameAnswer, type RecordCheckConfidence, type StringKey, type RecordCheckRecordType, type RecordChecksView, type RecordSuggestionRow, type RecordCheckUnknownReason, type RecordUnknownRow,
} from '@bms2/shared';
import { Button, Pill } from '../../components/controls';
import { LoadFailure } from '../../components/composites';
import { post, useApi } from './api';
import { AssumedPill, announcePersonHistory } from './PersonHistoryPanels';
import { JobMergeWindow } from './JobMergeWindow';
import styles from './RecordSuggestions.module.css';

/**
 * 🔒 The validation study on a Person and a Company page (piece 13,
 * *Suggestions and the Check act*; decision 5382): the Check act in the title
 * band and the Suggestions tile. Both read the record's own checks route, so
 * the page's one answer stays the size it was; a press or a decision tells the
 * other to read again. While a check waits the tile reads Checking and asks
 * again every few seconds until its marks land.
 */

/** How often a waiting check is read again; the rider works on a one-minute tick. */
export const RECORD_CHECK_POLL_MS = 10_000;
const CHANGED = 'bms2:record-checks';

function announce(path: string): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(CHANGED, { detail: path }));
}

/** The record's checks, read again when either half writes and while a check waits. */
export function useRecordChecks(recordType: RecordCheckRecordType, recordId: string, apiBaseUrl: string, pollMs = RECORD_CHECK_POLL_MS) {
  const path = recordChecksPath(recordType, recordId);
  const fetched = useApi<RecordChecksView>(path, apiBaseUrl);
  const { reload } = fetched;
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const heard = (event: Event) => {
      if ((event as CustomEvent<string>).detail === path) reload();
    };
    window.addEventListener(CHANGED, heard);
    return () => window.removeEventListener(CHANGED, heard);
  }, [path, reload]);
  const waiting = fetched.state.kind === 'ready' && fetched.state.data.waiting;
  useEffect(() => {
    if (!waiting) return undefined;
    const timer = setInterval(reload, pollMs);
    return () => clearInterval(timer);
  }, [waiting, reload, pollMs]);
  return { path, fetched };
}

const CONFIDENCE_KEYS: Readonly<Record<RecordCheckConfidence, StringKey>> = { High: 'recordChecks.confidence.high', Medium: 'recordChecks.confidence.medium', Low: 'recordChecks.confidence.low' };

const PILL_TONES: Readonly<Record<RecordCheckConfidence, { foreground: string; background: string }>> = {
  High: { foreground: 'var(--status-good-text)', background: 'var(--status-good-bg)' },
  Medium: { foreground: 'var(--status-warning-text)', background: 'var(--status-warning-bg)' },
  Low: { foreground: 'var(--status-inactive-text)', background: 'var(--status-inactive-bg)' },
};

export function ConfidencePill({ confidence }: { confidence: RecordCheckConfidence }) {
  return <Pill size={Size.Small} text={t(CONFIDENCE_KEYS[confidence])} {...PILL_TONES[confidence]} outlined />;
}

export interface RecordCheckActProps {
  recordType: RecordCheckRecordType;
  recordId: string;
  apiBaseUrl: string;
  onError: (message: string) => void;
}

/** 🔒 The Check act: offered to a writer of the record's module, shut with its sentence while a check waits or the cap is reached, and not drawn while no adapter is on (decision 6680). */
export function RecordCheckAct({ recordType, recordId, apiBaseUrl, onError }: RecordCheckActProps) {
  const { path, fetched } = useRecordChecks(recordType, recordId, apiBaseUrl);
  const [pressing, setPressing] = useState(false);
  if (fetched.state.kind !== 'ready' || !fetched.state.data.offered) return null;
  const view = fetched.state.data;
  // 🔒 Decision 6680: with no lookup turned on there is nothing to press, so the act and its sentence are not drawn at all.
  if (view.shut === 'noAdapter') return null;
  const press = async () => {
    setPressing(true);
    const pressed = await post<{ rows: number }>(path, {}, apiBaseUrl);
    setPressing(false);
    if (!pressed.ok) onError(pressed.message);
    announce(path);
  };
  return (
    <span className={styles.act} data-record-check-act={view.shut ?? 'open'}>
      <Button size={Size.Small} text={[view.waiting ? t('recordChecks.checking') : t('recordChecks.check')]} actions={[() => void press()]} {...(pressing || view.shut ? { disabled: true } : {})} />
      {view.shut && view.shut !== 'running' && view.shutSentence ? <span className={styles.shut} data-record-check-shut="true">{view.shutSentence}</span> : null}
    </span>
  );
}

export interface SuggestionsPanelProps {
  recordType: RecordCheckRecordType;
  recordId: string;
  zone: string;
  dateFormat: DateFormat;
  apiBaseUrl: string;
  /** An employer or an address opens its own window prefilled; the page owns the window. */
  onOpenWindow: (row: RecordSuggestionRow, stamp: () => Promise<void>) => void;
  /** The page reads its answer again after an Accept wrote a field. */
  onWritten: () => void;
  pollMs?: number;
}

/** 🔒 The Suggestions tile: each undecided Differs mark, newest first, with Accept and Dismiss; the line under the title says when the record was last checked. */
export function SuggestionsPanel({ recordType, recordId, zone, dateFormat, apiBaseUrl, onOpenWindow, onWritten, pollMs }: SuggestionsPanelProps) {
  const { path, fetched } = useRecordChecks(recordType, recordId, apiBaseUrl, pollMs);
  const [busy, setBusy] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  // 🔒 Decision 6440: the Job History row whose Merge window is open, and the months the last Merge left alone.
  const [merging, setMerging] = useState<RecordSuggestionRow | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // 🔒 Decision 6900: the Unknown count opens the fields the check found nothing for.
  const [showUnknown, setShowUnknown] = useState(false);
  if (fetched.state.kind === 'loading') return <p className={styles.quiet}>{t('async.loading')}</p>;
  if (fetched.state.kind === 'failed') return <LoadFailure state={fetched.state} onRetry={fetched.reload} />;
  if (fetched.state.kind !== 'ready') return null;
  const view = fetched.state.data;

  const decide = async (row: RecordSuggestionRow, act: 'accept' | 'dismiss', body: Record<string, unknown> = {}) => {
    setBusy(row.id);
    setRefusal(null);
    setNotice(null);
    const done = await post<{ leftAlone?: string | null }>(recordCheckActPath(recordType, recordId, row.id, act), body, apiBaseUrl);
    setBusy(null);
    setMerging(null);
    if (!done.ok) setRefusal(done.message);
    else if (done.data?.leftAlone) setNotice(fill(t('recordChecks.jobMerge.leftAloneDone'), { months: done.data?.leftAlone ?? '' }));
    announce(path);
    if (done.ok && act === 'accept') {
      // 🔒 Decision 6180: an Accept may write a school, a skill or a profile fact, which their own tiles read again.
      announcePersonHistory();
      onWritten();
    }
  };
  /** 🔒 Decision 7150: Same Company writes the suggested name on the employment's company, and the tile says what it wrote. */
  const sameCompany = async (row: RecordSuggestionRow) => {
    setBusy(row.id);
    setRefusal(null);
    setNotice(null);
    const done = await post<OtherNameAnswer>(recordCheckActPath(recordType, recordId, row.id, 'other-name'), {}, apiBaseUrl);
    setBusy(null);
    if (!done.ok) setRefusal(done.message);
    else if (done.data) setNotice(fill(t('recordChecks.sameCompanyDone'), { name: done.data?.name ?? '', company: done.data?.companyName ?? '' }));
    announce(path);
  };
  const accept = (row: RecordSuggestionRow) => {
    if (row.opensWindow) onOpenWindow(row, () => decide(row, 'accept', { recorded: true }));
    // 🔒 Decision 6440: a Merge that would move the employment's dates asks Keep Dates or Modify Dates first.
    // 🔒 Decision 6444: a row whose employment moved to another company asks Re-pin first.
    // 🔒 Decision 7330: only an entry that ends an open employment, or a retirement ending one, asks in a window.
    else if (row.repins || row.jobMerge?.ends) setMerging(row);
    else void decide(row, 'accept');
  };

  /** 🔒 Decision 7360: Add as a New Site opens the Add Site window the Address Accept opens, and the same stamp reads the row Accepted once the site is saved. */
  const addAsNewSite = (row: RecordSuggestionRow) => onOpenWindow(row, () => decide(row, 'accept', { recorded: true }));

  const line = view.lastCheckedAt
    ? fill(t('recordChecks.lastChecked'), { when: formatDateTime(view.lastCheckedAt, zone, dateFormat), confirmed: String(view.confirmed), unknown: String(view.unknown) })
    : t('recordChecks.notChecked');
  return (
    <div className={styles.panel} data-record-suggestions={recordId}>
      <p className={styles.line} data-record-checked-line="true">
        {view.waiting ? <span className={styles.checking} data-record-checking="true">{t('recordChecks.checking')}</span> : null}
        <span>{line}</span>
        {(view.unknownRows ?? []).length > 0 ? (
          <Button size={Size.Small} text={[showUnknown ? t('recordChecks.unknown.hide') : t('recordChecks.unknown.show')]} actions={[() => setShowUnknown((open) => !open)]} />
        ) : null}
      </p>
      {showUnknown && (view.unknownRows ?? []).length > 0 ? <UnknownList rows={view.unknownRows} /> : null}
      {refusal ? <p className={styles.refusal} role="alert">{refusal}</p> : null}
      {notice ? <p className={styles.quiet} role="status" data-job-merge-done="true" data-record-check-notice="true">{notice}</p> : null}
      {merging ? <JobMergeWindow key={merging.id} row={merging} dateFormat={dateFormat} onCancel={() => setMerging(null)} onMerge={(body) => decide(merging, 'accept', body)} /> : null}
      {view.rows.length === 0 && !view.waiting ? <p className={styles.quiet}>{t('recordChecks.none')}</p> : null}
      {view.rows.length > 0 ? (
        <ul className={styles.rows}>
          {view.rows.map((row) => (
            <li key={row.id} className={styles.row} data-suggestion-field={row.field} data-suggestion-id={row.id}>
              <div className={styles.head}>
                <span className={styles.field}>{RECORD_CHECK_FIELD_LABELS[row.field]}</span>
                {row.employmentLabel ? <span className={styles.quiet}>{fill(t('recordChecks.on'), { label: row.employmentLabel ?? '' })}</span> : null}
                <ConfidencePill confidence={row.confidence} />
                {row.assumed ? <AssumedPill /> : null}
              </div>
              <p className={styles.values}>
                <span className={styles.label}>{t('recordChecks.here')}</span>
                <span className={row.valueHere ? styles.here : styles.quiet}>{row.valueHere ?? t('recordChecks.noneHere')}</span>
              </p>
              <p className={styles.values}>
                <span className={styles.label}>{t('recordChecks.suggested')}</span>
                <span className={styles.suggested} data-suggestion-sealed={row.sealed ? 'true' : 'false'}>{row.suggested}</span>
              </p>
              {detailLines(row.field, row.detail).length > 0 ? (
                <dl className={styles.detail} data-suggestion-detail="true">
                  {detailLines(row.field, row.detail).map((line) => (
                    <div key={line.label} className={styles.detailLine}>
                      <dt className={styles.label}>{line.label}</dt>
                      <dd className={styles.detailValue}>
                        {line.link ? <a href={line.link} target="_blank" rel="noopener noreferrer" data-suggestion-detail-link="true">{line.value}</a> : line.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : null}
              {row.postOfficeBox ? <p className={styles.source} data-suggestion-post-office-box="true">{t('recordChecks.postOfficeBox')}</p> : null}
              {row.sameCompanyAs ? <p className={styles.source} data-suggestion-same-company="true">{fill(t('recordChecks.sameCompanyLine'), { company: row.sameCompanyAs ?? '' })}</p> : null}
              {row.jobEntry ? <p className={styles.source} data-suggestion-job-entry={row.jobEntry.kind}>{fill(t(JOB_ENTRY_LINE_KEYS[row.jobEntry.kind]), { label: row.jobEntry?.label ?? '' })}</p> : null}
              {row.jobEntry?.kind === 'Title' && row.jobMerge?.leftAlone ? <p className={styles.source} data-suggestion-left-alone="true">{fill(t('recordChecks.jobMerge.leftAlone'), { months: row.jobMerge?.leftAlone ?? '' })}</p> : null}
              {row.merges && !row.jobEntry ? <p className={styles.source} data-suggestion-merges="true">{fill(t('recordChecks.mergesInto'), { label: row.merges ?? '' })}</p> : null}
              <p className={styles.source}>
                {row.sourceUrl ? (
                  <a href={row.sourceUrl} target="_blank" rel="noopener noreferrer" data-suggestion-source-link="true">{row.source}</a>
                ) : (
                  <span>{row.source}</span>
                )}
              </p>
              <div className={styles.buttons}>
                {row.offersNewSite && row.postOfficeBox ? newSiteButton(row, addAsNewSite, busy) : null}
                {row.accepts && !(row.jobEntry?.kind === 'Retired' && !row.jobMerge) ? <Button size={Size.Small} text={[row.repins ? t('recordChecks.jobMerge.repin') : row.jobEntry ? t(JOB_ENTRY_BUTTON_KEYS[row.jobEntry.kind]) : row.merges ? t('recordChecks.merge') : t('recordChecks.accept')]} actions={[() => accept(row)]} {...(busy === row.id ? { disabled: true } : {})} /> : null}
                {row.offersNewSite && !row.postOfficeBox ? newSiteButton(row, addAsNewSite, busy) : null}
                {row.offersOtherName ? <Button size={Size.Small} text={[t('recordChecks.sameCompany')]} actions={[() => void sameCompany(row)]} {...(busy === row.id ? { disabled: true } : {})} /> : null}
                <Button size={Size.Small} text={[t('recordChecks.dismiss')]} actions={[() => void decide(row, 'dismiss')]} {...(busy === row.id ? { disabled: true } : {})} />
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** 🔒 Decision 7360: a company Address row's Add as a New Site, led with on a post office box. */
function newSiteButton(row: RecordSuggestionRow, open: (row: RecordSuggestionRow) => void, busy: string | null) {
  return <Button size={Size.Small} text={[t('recordChecks.addAsNewSite')]} actions={[() => open(row)]} {...(busy === row.id ? { disabled: true } : {})} />;
}

/** 🔒 Decision 7330: the line a Job History card reads for what its button does, and the button's words. */
const JOB_ENTRY_LINE_KEYS: Readonly<Record<JobEntryKind, StringKey>> = { Title: 'recordChecks.addsTitle', Employment: 'recordChecks.addsEmployment', Retired: 'recordChecks.retiredFrom' };
const JOB_ENTRY_BUTTON_KEYS: Readonly<Record<JobEntryKind, StringKey>> = { Title: 'recordChecks.addTitle', Employment: 'recordChecks.addEmployment', Retired: 'recordChecks.endEmploymentThere' };

/** Decision 6900: each reason's words. */
const UNKNOWN_REASON_KEYS: Readonly<Record<RecordCheckUnknownReason, StringKey>> = {
  NoSource: 'recordChecks.unknown.noSource',
  NoAdapter: 'recordChecks.unknown.noAdapter',
  CapReached: 'recordChecks.unknown.capReached',
  CheckFailed: 'recordChecks.unknown.checkFailed',
};

/** 🔒 Decision 6900: the latest check's Unknown fields, read-only, each with its employment and its reason. */
function UnknownList({ rows }: { rows: readonly RecordUnknownRow[] }) {
  return (
    <div className={styles.unknown} data-record-unknown="true">
      <p className={styles.quiet}>{t('recordChecks.unknown.intro')}</p>
      <ul className={styles.unknownRows}>
        {rows.map((row, index) => (
          <li key={`${row.field}:${row.employmentLabel ?? ''}:${index}`} className={styles.unknownRow} data-unknown-field={row.field} data-unknown-reason={row.reason}>
            <span className={styles.field}>{RECORD_CHECK_FIELD_LABELS[row.field]}</span>
            {row.employmentLabel ? <span className={styles.quiet}>{fill(t('recordChecks.on'), { label: row.employmentLabel ?? '' })}</span> : null}
            <span className={styles.quiet}>{t(UNKNOWN_REASON_KEYS[row.reason])}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
